import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, XCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { shipperQuoteService } from '@/data/mock-quotes';
import type { ShipperQuoteRequest, QuoteResponse } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { QuoteResponseDetailPanel } from '@/components/application/quotes/quote-response-detail-panel';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';

export const ShipperQuoteResponseDetailPage = () => {
    const { id, responseId } = useParams<{ id: string; responseId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation(['pages:quotes', 'common']);
    const [quoteRequest, setQuoteRequest] = useState<ShipperQuoteRequest | null>(null);
    const [quoteResponse, setQuoteResponse] = useState<QuoteResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmAcceptId, setConfirmAcceptId] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            if (!id || !responseId) return;

            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 300));

            const request = shipperQuoteService.getById(id);
            if (request) {
                setQuoteRequest(request);
                const response = request.receivedQuotes.find(qr => qr.id === responseId);
                if (response) {
                    setQuoteResponse(response);
                }
            }

            setIsLoading(false);
        };
        loadData();
    }, [id, responseId]);

    const handleBack = () => {
        navigate(`/shipper/quotes/${id}?tab=responses`);
    };

    const handleAcceptQuote = (quoteId: string) => {
        // Show confirmation modal
        setConfirmAcceptId(quoteId);
    };

    const handleRejectQuote = (quoteId: string) => {
        if (id && quoteRequest) {
            const updated = shipperQuoteService.updateQuoteResponseStatus(id, quoteId, 'rejected');
            if (updated) {
                setQuoteRequest(updated);
                const response = updated.receivedQuotes.find(qr => qr.id === quoteId);
                if (response) {
                    setQuoteResponse(response);
                }
            }
        }
    };

    const handleRequestModification = (quoteId: string, body?: string, subject?: string) => {
        if (body !== undefined && body.trim()) {
            if (id && quoteRequest) {
                const updated = shipperQuoteService.saveModificationRequestBody(id, quoteId, body, subject);
                if (updated) {
                    setQuoteRequest(updated);
                    const response = updated.receivedQuotes.find(qr => qr.id === quoteId);
                    if (response) {
                        setQuoteResponse(response);
                    }
                }
            }
        }
    };

    const handleMarkAsRead = (quoteId: string) => {
        if (id && quoteRequest) {
            const updated = shipperQuoteService.markQuoteAsRead(id, quoteId);
            if (updated) {
                setQuoteRequest(updated);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingIndicator />
            </div>
        );
    }

    if (!quoteRequest || !quoteResponse) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <p className="text-lg text-primary mb-4">{t('shipper.detail.notFound') || 'Quote response not found'}</p>
                <Button onClick={handleBack} color="secondary" size="md" iconLeading={ArrowLeft}>
                    {t('shipper.detail.back') || 'Back'}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-secondary bg-primary">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={handleBack}
                        color="secondary"
                        size="sm"
                        iconLeading={ArrowLeft}
                    >
                        {t('shipper.detail.back') || 'Back'}
                    </Button>
                    <div className="flex-1">
                        <h1 className="text-xl font-semibold text-primary">{quoteResponse.subject}</h1>
                        <p className="text-sm text-tertiary">
                            {t('shipper.detail.sentAt') || 'Sent At'}: {quoteRequest.sentAt.toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                <QuoteResponseDetailPanel
                    quote={quoteResponse}
                    onClose={handleBack}
                    onMarkAsRead={handleMarkAsRead}
                    onAccept={handleAcceptQuote}
                    onReject={handleRejectQuote}
                    onRequestModification={handleRequestModification}
                    quoteRequestStatus={quoteRequest.status}
                    initialModificationEditorOpen={searchParams.get('modification') === 'true'}
                />
            </div>

            {/* Confirm Accept Modal */}
            {confirmAcceptId && (
                <ModalOverlay isOpen={!!confirmAcceptId} onOpenChange={() => setConfirmAcceptId(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('shipper.responses.confirmAcceptTitle')}
                                    </h2>
                                    <button
                                        onClick={() => setConfirmAcceptId(null)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common:close') || 'Close'}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-tertiary mb-6">
                                    {t('shipper.responses.confirmAcceptMessage')}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={() => setConfirmAcceptId(null)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('common:cancel') || 'Annulla'}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (id && quoteRequest && confirmAcceptId) {
                                                const updated = shipperQuoteService.updateQuoteResponseStatus(id, confirmAcceptId, 'accepted');
                                                if (updated) {
                                                    setQuoteRequest(updated);
                                                    const response = updated.receivedQuotes.find(qr => qr.id === confirmAcceptId);
                                                    if (response) {
                                                        setQuoteResponse(response);
                                                    }
                                                }
                                            }
                                            setConfirmAcceptId(null);
                                        }}
                                        color="primary"
                                        size="md"
                                    >
                                        {t('shipper.responses.confirmAcceptConfirm')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

